import React, { Component } from 'react';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { getOffsetWithRelative } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';

interface DraggableProps {
    draggable?: boolean;
    scale: number;
    onDrag?: (e: any, sizeAtStart: any, rect: any, mode: MODES) => void;
    onDragStart?: (originalSize: any, e: any) => void;
    onDragStop?: (mode: MODES, e: any) => void;
    getSize: () => any;
    children?: React.ReactNode;
    pointerEvents?: string;
    relativeRotate?: number;
    relativeFlipH?: number;
    relativeFlipV?: number;
}

class Draggable extends Component<DraggableProps> {
    static displayName = 'Draggable';

    private dragStartTime: number | null;
    private startMousePosition: { x: number, y: number };
    private sizeAtStart: any;
    private isExistGroup: boolean;

    public render() {
        let draggableStyle;
        if (this.props.pointerEvents) {
            draggableStyle = {
                cursor: 'move',
                pointerEvents: this.props.pointerEvents ? this.props.pointerEvents : 'none',
            };
        }
        return (
            <div
                className={'draggable'}
                style={draggableStyle}
                onMouseDown={this.handleMouseDown}
                onTouchStart={this.handleTouchStart}
            >
                {this.props.children}
            </div>
        );
    }

    private getEventCoordinates = (e: any): { x: number, y: number } => ({
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
    });

    /*
     * Beging dragging. Store dragStartTime, which is used to prevent
     * unintentional drags
     */

    private startDrag = (e: any): void => {
        this.dragStartTime = Date.now();
        this.startMousePosition = this.getEventCoordinates(e);
        this.sizeAtStart = this.props.getSize();

        this.isExistGroup = (this.getSelectionService()?.selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP).length > 0);
    };

    /*
     * Stop dragging
     */

    private stopDrag = (e: any): void => {
        e.preventDefault();
        e.stopPropagation();

        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.stopDrag);
        document.removeEventListener('touchmove', this.handleMouseMove);
        document.removeEventListener('touchend', this.stopDrag);

        if (!this.dragStartTime) {
            this.props.onDragStop?.(MODES.MOVE, e);
        }
    };

    private handleMouseDown = (e: any): void => {
        if (!this.props.draggable) return;
        if (e.nativeEvent.which === 1) {
            document.addEventListener('mousemove', this.handleMouseMove);
            document.addEventListener('mouseup', this.stopDrag);
            this.startDrag(e);
        }
    };

    private handleTouchStart = (e: any): void => {
        if (!this.props.draggable) return;
        document.addEventListener('touchmove', this.handleMouseMove);
        document.addEventListener('touchend', this.stopDrag);
        this.startDrag(e);
    };

    /*
     * Handle mouse move.
     * We wait for 100ms and a delta >= 1 before emitting onDragStart
     */

    // eslint-disable-next-line complexity
    private handleMouseMove = (e: any): void => {
        e.preventDefault();
        const coords = this.getEventCoordinates(e);
        const delayTime = browser.isMobile ? -1 : 100;
        if (this.dragStartTime && Date.now() - this.dragStartTime > delayTime
            && (coords.x !== this.startMousePosition.x || coords.y !== this.startMousePosition.y)) {
            this.dragStartTime = null;
            this.props.onDragStart?.(this.sizeAtStart, e);
        } else if (!this.dragStartTime) {
            e.stopPropagation();
            const targetSize = { ...this.sizeAtStart };
            const windowZoom = browser.isMobile ? this.getMobileZoomService().mobileZoomScale : 1;
            let offsetX = (coords.x - this.startMousePosition.x) / this.props.scale / windowZoom;
            let offsetY = (coords.y - this.startMousePosition.y) / this.props.scale / windowZoom;
            if (e.shiftKey) {
                if (Math.abs(offsetX) > Math.abs(offsetY)) {
                    offsetY = 0;
                } else {
                    offsetX = 0;
                }
            }

            if (this.isExistGroup) {
                const offset = getOffsetWithRelative(this.props, offsetX, offsetY);
                offsetX = offset.offsetX;
                offsetY = offset.offsetY;
            }
            targetSize.left += offsetX;
            targetSize.top += offsetY;
            this.moveTo(e, targetSize);
        }
    };

    /*
     * Emit onDrag
     */

    private moveTo(e: any, rect: any): void {
        this.props.onDrag?.(e, this.sizeAtStart, rect, MODES.MOVE);
    }

    private getSelectionService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }

    private getMobileZoomService() {
        return (AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices)
            .viewableServiceLayer.mobileZoomService;
    }
}

export default Draggable;
